import useSaveTempDoc from "./useSaveTempDoc";
import { useToast } from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import { useNavigate } from "react-router-dom";
import { TempDocumentDetailResponseWithAny } from "../queries/useTempDocumentDetailQuery";
import useUpdateTempDoc from "./useUpdateTempDoc";
import getTempUpdateRequest from "features/document/utils/getTempUpdateRequest";
import getTempSaveRequest, {
  DocumentForm,
} from "features/document/utils/getTempSaveRequest";
import useFreeTags from "./useFreeTags";

interface UseDocumentTempSaveProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
  documentClassName: string;
  documentClassUuid: string;
  companyUuid?: string;
  getTagUuids: () => string[];
  setBeforeUnloadDisable: (disable: boolean) => void;
}

const useDocumentTempSave = <T extends DocumentForm>({
  tempDocumentDetail,
  documentClassName,
  documentClassUuid,
  companyUuid,
  getTagUuids,
  setBeforeUnloadDisable,
}: UseDocumentTempSaveProps) => {
  const navigate = useNavigate();

  const { getFreeTagUuids } = useFreeTags({
    tempDocumentDetail,
  });

  // 임시 저장 토스트
  const {
    open: openTempSaveToast,
    onOpenToast: onOpenTempSaveToast,
    onOpenChange: onOpenChangeTempSaveToast,
  } = useToast();

  const { saveTempDoc } = useSaveTempDoc();
  const { updateTempDoc } = useUpdateTempDoc();

  /**
   * @description 임시 저장 버튼 클릭 핸들러
   */
  const onTempSaveDoc = async ({
    form,
    provinceName,
    cityName,
    lastLevel,
  }: {
    form: T;
    provinceName?: string;
    cityName?: string;
    lastLevel?: number;
  }) => {
    if (!tempDocumentDetail) {
      const saveRequest = getTempSaveRequest({
        documentClassName,
        documentClassUuid,
        form,
        tagUuids: getTagUuids(),
        freeTagUuids: getFreeTagUuids(),
        companyUuid,
        provinceName,
        cityName,
        lastLevel,
      });

      saveTempDoc({
        saveRequest,
        successCallback() {
          onOpenTempSaveToast(() => {
            setBeforeUnloadDisable(true);
            navigate(ROUTER_PATH.home);
          });
        },
      });
    } else {
      const updateRequest = getTempUpdateRequest({
        tempDocumentDetail,
        companyUuid,
        form,
        tagUuids: getTagUuids(),
        freeTagUuids: getFreeTagUuids(),
        provinceName,
        cityName,
        lastLevel,
      });

      updateTempDoc({
        documentUuid: tempDocumentDetail.uuid,
        updateRequest,
        successCallback: () => {
          onOpenTempSaveToast(() => {
            setBeforeUnloadDisable(true);
            navigate(ROUTER_PATH.home);
          });
        },
      });
    }
  };

  return {
    openTempSaveToast,
    onOpenChangeTempSaveToast,
    onOpenTempSaveToast,
    onTempSaveDoc,
  };
};

export default useDocumentTempSave;
